import React, { useState, useEffect } from 'react';
import { Building, Plus, Trash2, Edit2, Check, X, AlertCircle, CreditCard, ChevronDown } from 'lucide-react';
import ClientAuthService from '../services/clientAuthService';

const MandateManagement = ({ onMandateChange }) => {
  const [mandates, setMandates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingAlias, setEditingAlias] = useState(null);
  const [aliasValue, setAliasValue] = useState('');
  const [processing, setProcessing] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);

  useEffect(() => {
    loadMandates();
  }, []);

  const loadMandates = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await ClientAuthService.getMandates();
      
      if (response.success) {
        setMandates(response.data || []);
        if (onMandateChange) {
          onMandateChange(response.data || []);
        }
      } else {
        setError(response.message || 'Failed to load payment methods');
      }
    } catch (error) {
      console.error('Error loading mandates:', error);
      setError('Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMandate = async () => {
    try {
      setProcessing(true);
      setError('');
      
      const successUrl = `${window.location.origin}/client/mandate-success`;
      
      const response = await ClientAuthService.createMandateSetup(successUrl);
      
      if (response.success) {
        // Redirect to GoCardless setup flow
        window.location.href = response.data.redirect_url;
      } else {
        setError(response.message || 'Failed to initiate mandate setup');
      }
    } catch (error) {
      console.error('Error creating mandate setup:', error);
      setError('Failed to start payment method setup');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteMandate = async (mandateId, bankName) => {
    if (!window.confirm(`Are you sure you want to delete the payment method from ${bankName}?\n\nThis action cannot be undone and will stop any future automatic payments.`)) {
      return;
    }

    try {
      setProcessing(true);
      setError('');
      
      const response = await ClientAuthService.api(`/payments/methods/${mandateId}`, {
        method: 'DELETE'
      });
      
      if (response.success) {
        await loadMandates();
        // Show success message
        alert('Payment method deleted successfully');
      } else {
        setError(response.message || 'Failed to delete payment method');
      }
    } catch (error) {
      console.error('Error deleting mandate:', error);
      setError('Failed to delete payment method');
    } finally {
      setProcessing(false);
    }
  };

  const handleSetDefault = async (mandateId, type) => {
    try {
      setProcessing(true);
      setError('');
      
      const response = await ClientAuthService.api(`/payments/methods/${mandateId}/default`, {
        method: 'PUT',
        body: JSON.stringify({
          type: type
        })
      });
      
      if (response.success) {
        await loadMandates();
      } else {
        setError(response.message || 'Failed to set default payment method');
      }
    } catch (error) {
      console.error('Error setting default mandate:', error);
      setError('Failed to set default payment method');
    } finally {
      setProcessing(false);
    }
  };

  const handleEditAlias = (mandateId, currentAlias) => {
    setEditingAlias(mandateId);
    setAliasValue(currentAlias || '');
  };

  const handleSaveAlias = async (mandateId) => {
    try {
      setProcessing(true);
      setError('');
      
      const response = await ClientAuthService.api(`/payments/methods/${mandateId}`, {
        method: 'PUT',
        body: JSON.stringify({
          alias: aliasValue
        })
      });
      
      if (response.success) {
        setEditingAlias(null);
        setAliasValue('');
        await loadMandates();
      } else {
        setError(response.message || 'Failed to update alias');
      }
    } catch (error) {
      console.error('Error updating alias:', error);
      setError('Failed to update alias');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingAlias(null);
    setAliasValue('');
  };

  const MandateCard = ({ mandate }) => {
    const isEditing = editingAlias === mandate.provider_method_id;
    const isDropdownOpen = dropdownOpen === mandate.provider_method_id;
    
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow mb-4">
        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-4 items-center">
            <div>
              <div className="flex items-start">
                <Building className="mr-3 text-blue-600 flex-shrink-0 mt-1" size={20} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center mb-2">
                    {isEditing ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          style={{ width: '200px' }}
                          value={aliasValue}
                          onChange={(e) => setAliasValue(e.target.value)}
                          placeholder="Enter alias"
                          onKeyPress={(e) => e.key === 'Enter' && handleSaveAlias(mandate.provider_method_id)}
                        />
                        <button
                          className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition duration-200"
                          onClick={() => handleSaveAlias(mandate.provider_method_id)}
                          disabled={processing}
                        >
                          <Check size={14} />
                        </button>
                        <button
                          className="p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded transition duration-200"
                          onClick={handleCancelEdit}
                          disabled={processing}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <h6 className="font-semibold text-gray-900 mr-2">
                          {mandate.alias || mandate.bank_name || 'Unknown Bank'}
                        </h6>
                        <button
                          className="p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded transition duration-200"
                          onClick={() => handleEditAlias(mandate.provider_method_id, mandate.alias)}
                          disabled={processing}
                        >
                          <Edit2 size={14} />
                        </button>
                      </>
                    )}
                  </div>
                  <div className="text-gray-500 text-sm mb-2">
                    {mandate.bank_name && mandate.alias ? `${mandate.bank_name} • ` : ''}
                    ****{mandate.account_number_last_four}
                    {mandate.sort_code && ` • ${mandate.sort_code}`}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {mandate.is_default_for_autopay && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Default for Auto-pay
                      </span>
                    )}
                    {mandate.is_default_for_topup && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Default for Top-up
                      </span>
                    )}
                    {mandate.mandate_status && (
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        mandate.mandate_status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {mandate.mandate_status}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end items-center gap-2">
              <div className="relative">
                <button
                  className="flex items-center px-3 py-1 text-sm border border-blue-600 text-blue-600 hover:bg-blue-50 rounded-md transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  type="button"
                  onClick={() => setDropdownOpen(isDropdownOpen ? null : mandate.provider_method_id)}
                  disabled={processing}
                >
                  Set Default
                  <ChevronDown size={14} className="ml-1" />
                </button>
                {isDropdownOpen && (
                  <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                    <div className="py-1">
                      <button
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center disabled:text-gray-400 disabled:cursor-not-allowed"
                        onClick={() => {
                          handleSetDefault(mandate.provider_method_id, 'autopay');
                          setDropdownOpen(null);
                        }}
                        disabled={mandate.is_default_for_autopay}
                      >
                        <CreditCard size={14} className="mr-2" />
                        For Auto-payments
                        {mandate.is_default_for_autopay && <Check size={14} className="ml-auto" />}
                      </button>
                      <button
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center disabled:text-gray-400 disabled:cursor-not-allowed"
                        onClick={() => {
                          handleSetDefault(mandate.provider_method_id, 'topup');
                          setDropdownOpen(null);
                        }}
                        disabled={mandate.is_default_for_topup}
                      >
                        <Plus size={14} className="mr-2" />
                        For Wallet Top-ups
                        {mandate.is_default_for_topup && <Check size={14} className="ml-auto" />}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              <button
                className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handleDeleteMandate(mandate.provider_method_id, mandate.bank_name)}
                disabled={processing}
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setDropdownOpen(null);
    };

    if (dropdownOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [dropdownOpen]);

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
        <span className="sr-only">Loading...</span>
        <p className="text-gray-600">Loading payment methods...</p>
      </div>
    );
  }

  return (
    <div className="mandate-management">
      <div className="flex justify-between items-center mb-6">
        <h5 className="text-lg font-semibold text-gray-900 mb-0 flex items-center">
          <Building className="mr-2" size={20} />
          Direct Debit Payment Methods
        </h5>
        <button
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition duration-200 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleAddMandate}
          disabled={processing}
        >
          {processing ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Setting up...
            </>
          ) : (
            <>
              <Plus size={16} className="mr-1" />
              Add Bank Account
            </>
          )}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4 flex items-start" role="alert">
          <AlertCircle size={16} className="mr-2 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {mandates.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 shadow">
          <div className="p-8 text-center">
            <Building className="mx-auto mb-4 text-gray-400" size={48} />
            <h6 className="text-lg font-semibold text-gray-900 mb-2">No Payment Methods Set Up</h6>
            <p className="text-gray-500 mb-4">
              Add a bank account to enable automatic payments and easy wallet top-ups.
            </p>
            <button
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition duration-200 flex items-center mx-auto disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleAddMandate}
              disabled={processing}
            >
              <Plus size={16} className="mr-1" />
              Add Your First Bank Account
            </button>
          </div>
        </div>
      ) : (
        <div>
          {mandates.map(mandate => (
            <MandateCard key={mandate._id} mandate={mandate} />
          ))}
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-4 rounded-md mt-6">
        <div className="flex items-start">
          <AlertCircle size={16} className="mr-2 mt-0.5 flex-shrink-0" />
          <div>
            <strong className="block mb-2">About Direct Debit:</strong>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>Payments are protected by the Direct Debit Guarantee</li>
              <li>You can cancel a Direct Debit at any time</li>
              <li>Payments typically take 3 working days to process</li>
              <li>You'll receive advance notice of any payments</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MandateManagement;